import React, { type ReactNode, useEffect, useRef, useState } from 'react';

import { useDebouncedCallback } from 'use-debounce';

import { IconButton } from '@atlaskit/button/new';
import ArrowLeftIcon from '@atlaskit/icon/core/arrow-left';
import ArrowRightIcon from '@atlaskit/icon/core/arrow-right';
import { Box, Inline, Stack, Text, xcss } from '@atlaskit/primitives';

const isScrollAtLeft = (elem: HTMLDivElement | null) => elem?.scrollLeft === 0;
const isScrollAtRight = (elem: HTMLDivElement | null) =>
	elem ? Math.abs(elem?.scrollWidth - elem?.scrollLeft - elem?.clientWidth) <= 1 : false;

const scrollContainerStyle = xcss({
	display: 'flex',
	overflowX: 'auto',
	scrollbarWidth: 'none',
	whiteSpace: 'nowrap',
});

interface Props {
	children: ReactNode;
	headingText: string;
	scrollOffset: number;
}

export const KnowledgeCarousel = ({ children, headingText, scrollOffset }: Props) => {
	const scrollContainerRef = useRef<HTMLDivElement>(null);

	const [shouldHideLeft, setShouldHideLeft] = useState(false);
	const [shouldHideRight, setShouldHideRight] = useState(false);

	useEffect(() => {
		setShouldHideLeft(isScrollAtLeft(scrollContainerRef.current));
		setShouldHideRight(isScrollAtRight(scrollContainerRef.current));
	}, []);

	const [onScroll] = useDebouncedCallback(() => {
		setShouldHideLeft(isScrollAtLeft(scrollContainerRef.current));
		setShouldHideRight(isScrollAtRight(scrollContainerRef.current));
	}, 10);

	const scrollLeft = () => {
		scrollContainerRef.current?.scrollBy({
			top: 0,
			left: -scrollOffset,
			behavior: 'smooth',
		});
	};

	const scrollRight = () => {
		scrollContainerRef.current?.scrollBy({
			top: 0,
			left: scrollOffset,
			behavior: 'smooth',
		});
	};

	return (
		<Stack space="space.100">
			<Inline spread="space-between" alignBlock="center">
				<Text size="large" weight="semibold">
					{headingText}
				</Text>
				<Inline space="space.050">
					{!shouldHideLeft && (
						<IconButton
							shape="circle"
							appearance="subtle"
							icon={ArrowLeftIcon}
							isTooltipDisabled
							label=""
							onClick={scrollLeft}
							spacing="compact"
						/>
					)}
					{!shouldHideRight && (
						<IconButton
							shape="circle"
							appearance="subtle"
							icon={ArrowRightIcon}
							isTooltipDisabled
							label=""
							onClick={scrollRight}
							spacing="compact"
						/>
					)}
				</Inline>
			</Inline>

			<Box ref={scrollContainerRef} onScroll={onScroll} xcss={scrollContainerStyle}>
				{children}
			</Box>
		</Stack>
	);
};
